import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { v4 as uuid } from "uuid";
import { DMVDataService } from "./dmv-data.service";
import { RegistrationResponse } from "../../models/dmv/registration.response";
import { RegistrationRequest } from "../../models/dmv/registration.request";
import { AgencyDmvConfigResponse } from "src/app/models/dmv/agency-config.response";
import { Registrations } from "src/app/models/dmv/registrations";
import { TagCatalogResponse } from "src/app/models/dmv/tagCatalogResponse";
import { DmvCostDataResponse } from "src/app/models/dmv/dmvCostDataResponse";
import { DmvCostDataRequest } from "src/app/models/dmv/dmv-cost-data.request";
import { Cart } from "src/app/models/dmv/cart";
import { DonationOrgsResponse } from "src/app/models/dmv/donationOrgsResponse";
import { RegistrationTypesResponse } from "src/app/models/dmv/registration-types.response";
import { Renewal } from "src/app/models/dmv/renewal";
import { RenewalResponse } from "src/app/models/dmv/renewal-response";
import { ServiceFeeAmountResponse } from "src/app/models/dmv/service-fee-amount.response";
import { TransactionIDResponse } from "src/app/models/dmv/transactionID.response";

import { RouteFrom } from "src/app/models/routeFrom";
import { PaymentDetails } from "src/app/models/dmv/paymentDetails";
import { MailingAddress } from "src/app/models/dmv/mailingAddress";
import { Registration } from "src/app/models/dmv/registration";
import { AccountCancellationInstructions } from "src/app/models/user/accountCancellationInstructions";
@Injectable({ providedIn: "root" })
export class DMVStoreService {
	constructor(private dmvDataService: DMVDataService) {}
	public mailingAddress: MailingAddress;
	public firstRegistration: Registration = null;
	public addressrequired: boolean = false;
	public isPrivacyCheck: boolean = false;

	/** ---------Start - Observable data service to get registrations -------------*/
	private readonly _registrations = new BehaviorSubject<RegistrationResponse>(
		null
	);
	readonly registrations$ = this._registrations.asObservable();
	// The getter will return the last value emitted
	get registrations(): RegistrationResponse {
		return this._registrations.getValue();
	}

	// Assigning a value will push it onto the observable
	set registrations(val: RegistrationResponse) {
		this._registrations.next(val);
	}

	//Data access by consumers
	public readonly registrationData$: Observable<RegistrationResponse> = this
		.registrations$;

	public selectedAgencyId: string = "";
	public addToProfileState: string = undefined;

	//Funding token
	//private fundingTokenFromProvider: string = "";

	get fundingToken(): string {
		return uuid();
	}
	//
	public sessionGUID: string;
	public sessionID: string;
	public isPaymentSucess: boolean;
	public routeFrom: RouteFrom;
	public targetPageNumber: number;
	public registrationSearchRequest: RegistrationRequest;
	public registrationsInCart: Cart;
	public states = [
		{ name: "Alabama", alpha: "AL" },
		{ name: "Alaska", alpha: "AK" },
		{ name: "Arizona", alpha: "AZ" },
		{ name: "Arkansas", alpha: "AR" },
		{ name: "California", alpha: "CA" },
		{ name: "Colorado", alpha: "CO" },
		{ name: "Connecticut", alpha: "CT" },
		{ name: "Delaware", alpha: "DE" },
		{ name: "District of Columbia", alpha: "DC" },
		{ name: "Florida", alpha: "FL" },
		{ name: "Georgia", alpha: "GA" },
		{ name: "Hawaii", alpha: "HI" },
		{ name: "Idaho", alpha: "ID" },
		{ name: "Illinois", alpha: "IL" },
		{ name: "Indiana", alpha: "IN" },
		{ name: "Iowa", alpha: "IA" },
		{ name: "Kansa", alpha: "KS" },
		{ name: "Kentucky", alpha: "KY" },
		{ name: "Lousiana", alpha: "LA" },
		{ name: "Maine", alpha: "ME" },
		{ name: "Maryland", alpha: "MD" },
		{ name: "Massachusetts", alpha: "MA" },
		{ name: "Michigan", alpha: "MI" },
		{ name: "Minnesota", alpha: "MN" },
		{ name: "Mississippi", alpha: "MS" },
		{ name: "Missouri", alpha: "MO" },
		{ name: "Montana", alpha: "MT" },
		{ name: "Nebraska", alpha: "NE" },
		{ name: "Nevada", alpha: "NV" },
		{ name: "New Hampshire", alpha: "NH" },
		{ name: "New Jersey", alpha: "NJ" },
		{ name: "New Mexico", alpha: "NM" },
		{ name: "New York", alpha: "NY" },
		{ name: "North Carolina", alpha: "NC" },
		{ name: "North Dakota", alpha: "ND" },
		{ name: "Ohio", alpha: "OH" },
		{ name: "Oklahoma", alpha: "OK" },
		{ name: "Oregon", alpha: "OR" },
		{ name: "Pennsylvania", alpha: "PA" },
		{ name: "Rhode Island", alpha: "RI" },
		{ name: "South Carolina", alpha: "SC" },
		{ name: "South Dakota", alpha: "SD" },
		{ name: "Tennessee", alpha: "TN" },
		{ name: "Texas", alpha: "TX" },
		{ name: "Utah", alpha: "UT" },
		{ name: "Vermont", alpha: "VT" },
		{ name: "Virginia", alpha: "VA" },
		{ name: "Washington", alpha: "WA" },
		{ name: "West Virginia", alpha: "WV" },
		{ name: "Wisconsin", alpha: "WI" },
		{ name: "Wyoming", alpha: "WY" }
	];
	/** ---------------------------------------------------------------------------  */

	/** ---------Start - Observable data service to get agency config ---------------*/
	private readonly _agencyConfig = new BehaviorSubject<AgencyDmvConfigResponse>(
		null
	);

	// Expose the observable$ part (read only stream)
	readonly agencyConfig$ = this._agencyConfig.asObservable();

	// The getter will return the last value emitted
	get agencyConfig(): AgencyDmvConfigResponse {
		return this._agencyConfig.getValue();
	}

	// Assigning a value will push it onto the observable
	set agencyConfig(val: AgencyDmvConfigResponse) {
		this._agencyConfig.next(val);
	}

	//Data access by consumers
	public readonly agencyConfigData$ = this.agencyConfig$;

	private readonly _cancellationToken = new BehaviorSubject<
		AccountCancellationInstructions
	>(null);

	// Expose the observable$ part (read only stream)
	readonly cancellationToken$ = this._cancellationToken.asObservable();

	// The getter will return the last value emitted
	get cancellationTokenData(): AccountCancellationInstructions {
		return this._cancellationToken.getValue();
	}

	// Assigning a value will push it onto the observable
	set cancellationTokenData(val: AccountCancellationInstructions) {
		this._cancellationToken.next(val);
	}

	//Data access by consumers
	public readonly cancellationTokengData$ = this.cancellationToken$;

	/** ---------Start - Observable data service to get agency config ---------------*/
	private readonly _tagCatalog = new BehaviorSubject<TagCatalogResponse>(null);

	// Expose the observable$ part (read only stream)
	readonly tagCatalog$ = this._tagCatalog.asObservable();

	// The getter will return the last value emitted
	get tagCatalog(): TagCatalogResponse {
		return this._tagCatalog.getValue();
	}

	// Assigning a value will push it onto the observable
	set tagCatalog(val: TagCatalogResponse) {
		this._tagCatalog.next(val);
	}

	//Data access by consumers
	public readonly tagCatalogData$ = this.tagCatalog$;
	/** ---------------------------------------------------------------------------  */

	private readonly _donationOrgs = new BehaviorSubject<DonationOrgsResponse>(
		null
	);

	// Expose the observable$ part (read only stream)
	readonly donationOrgs$ = this._donationOrgs.asObservable();

	// The getter will return the last value emitted
	get donationOrgs(): DonationOrgsResponse {
		return this._donationOrgs.getValue();
	}

	// Assigning a value will push it onto the observable
	set donationOrgs(val: DonationOrgsResponse) {
		this._donationOrgs.next(val);
	}

	//Data access by consumers
	public readonly donationOrgsData$ = this.donationOrgs$;

	/** ---------------------------------------------------------------------------  */

	private readonly _registrationTypes = new BehaviorSubject<
		RegistrationTypesResponse
	>(null);

	// Expose the observable$ part (read only stream)
	readonly registrationTypes$ = this._registrationTypes.asObservable();

	// The getter will return the last value emitted
	get registrationTypes(): RegistrationTypesResponse {
		return this._registrationTypes.getValue();
	}

	// Assigning a value will push it onto the observable
	set registrationTypes(val: RegistrationTypesResponse) {
		this._registrationTypes.next(val);
	}

	//Data access by consumers
	public readonly registrationTypesData$ = this.registrationTypes$;

	/** ---------------------------------------------------------------------------  */

	private readonly _dmvCost = new BehaviorSubject<DmvCostDataResponse>(null);

	// Expose the observable$ part (read only stream)
	readonly dmvCost$ = this._dmvCost.asObservable();

	// The getter will return the last value emitted
	get dmvCost(): DmvCostDataResponse {
		return this._dmvCost.getValue();
	}

	// Assigning a value will push it onto the observable
	set dmvCost(val: DmvCostDataResponse) {
		this._dmvCost.next(val);
	}

	//Data access by consumers
	public readonly dmvCostData$ = this.dmvCost$;
	/** ---------------------------------------------------------------------------  */

	private readonly _renewal = new BehaviorSubject<RenewalResponse>(null);

	// Expose the observable$ part (read only stream)
	readonly renewal$ = this._renewal.asObservable();

	// The getter will return the last value emitted
	get renewal(): RenewalResponse {
		return this._renewal.getValue();
	}

	// Assigning a value will push it onto the observable
	set renewal(val: RenewalResponse) {
		this._renewal.next(val);
	}

	//Data access by consumers
	public readonly renewalData$ = this.renewal$;
	/** ---------------------------------------------------------------------------  */

	private readonly _serviceFeeAmount = new BehaviorSubject<
		ServiceFeeAmountResponse
	>(null);

	// Expose the observable$ part (read only stream)
	readonly serviceFeeAmount$ = this._serviceFeeAmount.asObservable();

	// The getter will return the last value emitted
	get serviceFeeAmount(): ServiceFeeAmountResponse {
		return this._serviceFeeAmount.getValue();
	}

	// Assigning a value will push it onto the observable
	set serviceFeeAmount(val: ServiceFeeAmountResponse) {
		this._serviceFeeAmount.next(val);
	}

	//Data access by consumers
	public readonly serviceFeeAmountData$ = this.serviceFeeAmount$;
	/** ---------------------------------------------------------------------------  */

	private readonly _renewalSubmit = new BehaviorSubject<TransactionIDResponse>(
		null
	);

	// Expose the observable$ part (read only stream)
	readonly renewalSubmit$ = this._renewalSubmit.asObservable();

	// The getter will return the last value emitted
	get renewalSubmit(): TransactionIDResponse {
		return this._renewalSubmit.getValue();
	}

	// Assigning a value will push it onto the observable
	set renewalSubmit(val: TransactionIDResponse) {
		this._renewalSubmit.next(val);
	}

	//Payment URL
	//public paymentURL = "https://qavsuite.pnpit.com/vembed/payment-frame?agencyid=%27cssexample%27&accesstoken=%27abc-123%27&firstName=%27Test%27&lastName=%27User%27";
	public isPaymentSuccess: boolean = false;

	public paymentDetails: PaymentDetails;

	//Data access by consumers
	public readonly renewalSubmitData$ = this.renewalSubmit$;
	/** ---------------------------------------------------------------------------  */

	public findTagCatalog() {
		this.dmvDataService.getTagCatalogResponse(this.sessionID).subscribe(
			response => {
				if (response) {
					this.tagCatalog = response;
				}
			},
			error => {
				error.status = 408;
				this.tagCatalog = error;
			}
		);
	}

	public findDonationOrgs() {
		this.dmvDataService.getDonationOrgsResponse(this.sessionID).subscribe(
			response => {
				if (response) {
					this.donationOrgs = response;
				}
			},
			error => {
				error.status = 408;
				this.donationOrgs = error;
			}
		);
	}

	public updateAddress() {
		this.registrationsInCart.addressComefrom = undefined;
		if (this.registrationsInCart.selectedRegistrations) {
			this.firstRegistration = this.registrationsInCart.selectedRegistrations[0];
		}
		this.mailingAddress = Object.create(null);
		this.mailingAddress.city = "";
		this.mailingAddress.billigAddress1 = "";
		this.mailingAddress.billigAddress2 = "";
		this.mailingAddress.countryCode = "";
		this.mailingAddress.state = "";
		this.mailingAddress.zip = "";

		if (this.firstRegistration && this.firstRegistration.registrantAddress) {
			this.mailingAddress.city = this.firstRegistration.registrantAddress
				? this.firstRegistration.registrantAddress.city
				: "";
			this.mailingAddress.billigAddress1 = this.firstRegistration
				.registrantAddress
				? this.firstRegistration.registrantAddress.line1
				: "";
			this.mailingAddress.billigAddress2 = this.firstRegistration
				.registrantAddress
				? this.firstRegistration.registrantAddress.line2
				: "";
			this.mailingAddress.countryCode = this.firstRegistration.registrantAddress
				? this.firstRegistration.registrantAddress.countryCode
				: "";
			this.mailingAddress.state = this.firstRegistration.registrantAddress
				? this.firstRegistration.registrantAddress.stateCode
				: "";
			this.mailingAddress.zip = this.firstRegistration.registrantAddress
				? this.firstRegistration.registrantAddress.postalCode
				: "";
			this.registrationsInCart.hasAddress = true;
		} else {
			this.registrationsInCart.hasAddress = false;
		}

		this.mailingAddress.registrantName =
			this.firstRegistration && this.firstRegistration.registrantName
				? this.firstRegistration.registrantName
				: "";
		this.mailingAddress.updateAddress = false;
		this.registrationsInCart.firstIteration = false;
		this.mailingAddress.updateAddressCheckBox = false;
		this.registrationsInCart.pickupInPerson = undefined;
		this.registrationsInCart.mailingAddress = this.mailingAddress;
	}

	public findRegistrationTypes() {
		this.dmvDataService.getRegistrationTypesResponse(this.sessionID).subscribe(
			response => {
				if (response) {
					this.registrationTypes = response;
				}
			},
			error => {
				error.status = 408;
				this.registrationTypes = error;
			}
		);
	}
	public findRegistrations(registrationRequest: RegistrationRequest) {
		this.dmvDataService
			.getRegistrationResponse(
				registrationRequest.agencyID,
				registrationRequest.pin,
				registrationRequest.dateOfBirth,
				registrationRequest.licensePlate,
				registrationRequest.expirationDate,
				registrationRequest.pageSize,
				registrationRequest.targetPageNumber,
				this.sessionID,
				registrationRequest.qrCode,
				registrationRequest.token
			)
			.subscribe(
				response => {
					if (response) {
						this.registrations = response;
						//console.log(registrationResponse);
					}
				},
				error => {
					error.status = 408;
					this.registrations = error;
				}
			);
	}

	public findRenewal(request: Renewal, agencyID: string) {
		this.dmvDataService
			.getRenewalResponse(request, agencyID, this.sessionID)
			.subscribe(
				response => {
					if (response) {
						this.renewal = response;
						//console.log(registrationResponse);
					}
				},
				error => {
					error.status = 408;
					this.renewal = error;
				}
			);
	}

	public getServiceFees(renewalToken: string, agencyID: string) {
		this.dmvDataService
			.getServiceFeesResponse(renewalToken, agencyID, this.sessionID)
			.subscribe(
				response => {
					if (response) {
						this.serviceFeeAmount = response;
					}
				},
				error => {
					error.status = 408;
					this.serviceFeeAmount = error;
				}
			);
	}

	public submitRenewal(renewalToken: string, agencyID: string) {
		this.dmvDataService
			.getRenewalSubmitResponse(renewalToken, agencyID, this.sessionID)
			.subscribe(
				response => {
					if (response && response.status === 200) {
						this.renewalSubmit = response.body;
					}
				},
				error => {
					error.status = 408;
					this.renewalSubmit = error;
				}
			);
	}

	public findDmvCost(
		request: DmvCostDataRequest,
		agencyID: string,
		registrationID: number
	) {
		this.dmvDataService
			.getDmvCostResponse(
				request.newTagCode,
				request.renewalOptionID,
				agencyID,
				registrationID,
				this.sessionID
			)
			.subscribe(
				response => {
					if (response) {
						this.dmvCost = response;
						//console.log(registrationResponse);
					}
				},
				error => {
					error.status = 408;
					this.dmvCost = error;
				}
			);
	}
	public findAgencyConfigById(agencyID: string) {
		this.dmvDataService
			.getAgencyConfigResponse(agencyID, this.sessionID)
			.subscribe(
				response => {
					if (response) {
						this.agencyConfig = response;
					}
				},
				error => {
					error.status = 408;
					this.agencyConfig = error;
				}
			);
	}
}
